import { ReactNode, useEffect, useState } from 'react'
import { ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import antdZhTW from 'antd/locale/zh_TW'
import antdEnUS from 'antd/locale/en_US'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-tw'
import 'dayjs/locale/en'
import i18n, { DEFAULT_LANGUAGE, isSupportedLanguage, SupportedLanguage } from './config'

const ANTD_LOCALES: Record<SupportedLanguage, Locale> = {
  'zh-TW': antdZhTW,
  'en-US': antdEnUS,
}

const DAYJS_LOCALES: Record<SupportedLanguage, string> = {
  'zh-TW': 'zh-tw',
  'en-US': 'en',
}

const resolve = (language: string): SupportedLanguage =>
  isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE

/**
 * Keeps Ant Design's own copy (date pickers, pagination, empty states) and
 * dayjs formatting in step with the app language. Note this deliberately does
 * NOT touch dayjs.tz — Hong Kong is a business rule, not a locale preference.
 */
export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<SupportedLanguage>(() => resolve(i18n.language))

  useEffect(() => {
    const handleChange = (next: string) => setLanguage(resolve(next))
    i18n.on('languageChanged', handleChange)
    return () => {
      i18n.off('languageChanged', handleChange)
    }
  }, [])

  useEffect(() => {
    dayjs.locale(DAYJS_LOCALES[language])
  }, [language])

  return <ConfigProvider locale={ANTD_LOCALES[language]}>{children}</ConfigProvider>
}

export default I18nProvider
